import * as XLSX from 'xlsx';

// These editable-column dropdown options mirror the ones already used by the
// existing Pathway Stream template (Students.jsx / UploadPathwayStreams.jsx)
// so a sheet exported here can be filled in and re-uploaded at the existing
// /admin/students/pathway-stream-upload page without any format changes.
const STREAM_TEMPLATE_OPTIONS = ['Software Engineering', 'Network Engineering'];
const DELIVERY_STATUS_TEMPLATE_OPTIONS = [
  'academic delivery completed',
  'graduated',
  'certificate received',
  'certificate dispatched',
  'Dropped',
  'Transfer to another branch',
  'Transfer to another course',
];

const DELIVERY_STATUS_DESCRIPTIONS = [
  ['(blank / Ongoing)', 'Student is still actively going through the course — no delivery status has been set yet.'],
  ['academic delivery completed', 'All lectures/coursework for this student have been delivered; awaiting further processing.'],
  ['graduated', 'Student has officially graduated from the course.'],
  ['certificate received', 'The certificate has been received from the awarding body / university.'],
  ['certificate dispatched', 'The certificate has been dispatched / handed over to the student.'],
  ['Dropped', 'Student has dropped out and is no longer continuing the course.'],
  ['Transfer to another branch', 'Student has transferred to a different campus/branch.'],
  ['Transfer to another course', 'Student has transferred to a different course.'],
];

const STREAM_DESCRIPTIONS = [
  ['(blank / Not Set)', 'No stream has been assigned — applies to students outside the HND in Computing pathway, or not yet assigned one.'],
  ['Software Engineering', 'Student is following the Software Engineering stream (HND in Computing pathway).'],
  ['Network Engineering', 'Student is following the Network Engineering stream (HND in Computing pathway).'],
];

export function formatStatusLabel(status) {
  if (!status) return '—';
  return status
    .split(' ')
    .map((word) => (word.length ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

export function deliveryStatusBadgeClass(status) {
  switch ((status || '').toLowerCase()) {
    case 'academic delivery completed': return 'bg-blue-100 text-blue-700';
    case 'graduated': return 'bg-emerald-100 text-emerald-700';
    case 'certificate received':
    case 'certificate dispatched': return 'bg-indigo-100 text-indigo-700';
    case 'dropped': return 'bg-red-100 text-red-700';
    case 'transfer to another branch':
    case 'transfer to another course': return 'bg-orange-100 text-orange-700';
    default: return 'bg-teal-100 text-teal-700';
  }
}

export function deliveryStatusBadgeStyle(status) {
  switch ((status || '').toLowerCase()) {
    case 'academic delivery completed': return { background: '#dbeafe', color: '#1d4ed8' };
    case 'graduated': return { background: '#d1fae5', color: '#047857' };
    case 'certificate received':
    case 'certificate dispatched': return { background: '#e0e7ff', color: '#4338ca' };
    case 'dropped': return { background: '#fee2e2', color: '#b91c1c' };
    case 'transfer to another branch':
    case 'transfer to another course': return { background: '#ffedd5', color: '#c2410c' };
    default: return { background: '#ccfbf1', color: '#0f766e' };
  }
}

// Delivery status is null while a student is still actively going through the
// course — display that as "Ongoing" rather than a blank cell.
export function deliveryStatusLabel(status) {
  return status ? formatStatusLabel(status) : 'Ongoing';
}

// Lowercased, null-safe key for a student's delivery status — 'ongoing' when
// the student hasn't finished/left the course yet (deliveryStatus is null).
export function normalizedDeliveryStatus(status) {
  return status ? status.trim().toLowerCase() : 'ongoing';
}

export const STUDENT_STATUS_OPTIONS = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'academic delivery completed', label: 'Academic Delivery Completed' },
  { value: 'graduated', label: 'Graduated' },
  { value: 'certificate received', label: 'Certificate Received' },
  { value: 'certificate dispatched', label: 'Certificate Dispatched' },
  { value: 'dropped', label: 'Dropped' },
  { value: 'transfer to another branch', label: 'Transfer to Another Branch' },
  { value: 'transfer to another course', label: 'Transfer to Another Course' },
];

// Keeps only students whose delivery status is in `statusValues` (lowercased
// values, e.g. from STUDENT_STATUS_OPTIONS), dropping batches/courses left
// with no students. An empty/undefined `statusValues` means "no filter".
export function filterCoursesByStudentStatus(courses, statusValues) {
  if (!Array.isArray(statusValues) || statusValues.length === 0) return courses;
  const wanted = new Set(statusValues.map((s) => String(s).toLowerCase()));
  return courses
    .map((course) => ({
      ...course,
      batches: course.batches
        .map((batch) => ({
          ...batch,
          students: batch.students.filter((s) => wanted.has(normalizedDeliveryStatus(s.deliveryStatus))),
        }))
        .filter((batch) => batch.students.length > 0),
    }))
    .filter((course) => course.batches.length > 0);
}

// Per-status headcount for a batch's student list — keys match
// STUDENT_STATUS_OPTIONS values, used by the count-summary table view.
export function computeBatchStatusCounts(students) {
  const counts = {};
  STUDENT_STATUS_OPTIONS.forEach((opt) => {
    counts[opt.value] = 0;
  });
  (students || []).forEach((s) => {
    const key = normalizedDeliveryStatus(s.deliveryStatus);
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

export function batchStatusBadgeClass(status) {
  switch ((status || '').toLowerCase()) {
    case 'not started': return 'bg-gray-100 text-gray-600';
    case 'ongoing': return 'bg-emerald-100 text-emerald-700';
    case 'delivery completed': return 'bg-blue-100 text-blue-700';
    case 'batch completed': return 'bg-purple-100 text-purple-700';
    default: return 'bg-gray-100 text-gray-500';
  }
}

export function batchStatusBadgeStyle(status) {
  switch ((status || '').toLowerCase()) {
    case 'not started': return { background: '#f3f4f6', color: '#4b5563' };
    case 'ongoing': return { background: '#d1fae5', color: '#047857' };
    case 'delivery completed': return { background: '#dbeafe', color: '#1d4ed8' };
    case 'batch completed': return { background: '#ede9fe', color: '#6d28d9' };
    default: return { background: '#f3f4f6', color: '#6b7280' };
  }
}

// Flattens { student, batch } pairs into the row shape the export template
// needs — one row per student, carrying along the batch it was pulled from.
export function buildStudentExportRows(entries) {
  return entries.map(({ student, batch }) => ({
    studentId: student.studentId || '',
    studentName: student.studentName || '',
    registrationNumber: student.registrationNumber || '',
    batchName: batch.batchInternalName || batch.whatsappGroupName || batch.batchName || '',
    whatsappGroupName: batch.whatsappGroupName || '',
    deliveryStatus: student.deliveryStatus || '',
    stream: student.stream || '',
  }));
}

function buildInstructionsSheet() {
  const rows = [
    ['Active Batches & Student Status — Bulk Update Template'],
    [],
    ['How to use this sheet'],
    ['1. The "Student Status" sheet lists students from the batch/course/status you clicked Download on.'],
    ['2. "Current Delivery Status" and "Current Stream" show today\'s value for reference only — they are ignored on re-upload.'],
    ['3. Edit "Delivery Status" / "Stream" only for students you want to change. Leave a cell blank to leave that student unchanged.'],
    ['4. Use the dropdown arrow on each "Delivery Status" / "Stream" cell to pick a valid value — this avoids typos that would block the upload.'],
    ['5. When done, go to Admin > Students > Upload Pathway Stream Template (/admin/students/pathway-stream-upload) and upload this file.'],
    ['6. Rows are matched by Student ID + Registration Number, so do not edit those two columns.'],
    [],
    ['Delivery Status options'],
    ['Value', 'Meaning'],
    ...DELIVERY_STATUS_DESCRIPTIONS,
    [],
    ['Stream options'],
    ['Value', 'Meaning'],
    ...STREAM_DESCRIPTIONS,
  ];
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!cols'] = [{ wch: 30 }, { wch: 90 }];
  return sheet;
}

// Builds and downloads an .xlsx workbook in the exact column layout the
// existing /admin/students/pathway-stream-upload page expects, so a cell's
// exported sheet can be edited and re-uploaded there to bulk-update
// registrations.deliveryStatus / registrations.stream. Returns false (and
// downloads nothing) when there are no rows to export.
export function downloadStudentStatusTemplate(rows, filenameSuffix) {
  if (!Array.isArray(rows) || rows.length === 0) return false;

  const headers = [
    'Student ID',
    'Student Name',
    'Registration Number',
    'Batch Name',
    'Batch WhatsApp Group',
    'Current Delivery Status',
    'Delivery Status',
    'Current Stream',
    'Stream',
  ];

  const dataRows = rows.map((r) => [
    r.studentId,
    r.studentName,
    r.registrationNumber,
    r.batchName,
    r.whatsappGroupName,
    // "Current …" columns are reference-only (ignored on re-upload), so they
    // always show something readable — never a blank cell that looks broken.
    r.deliveryStatus || 'Ongoing (not set)',
    r.deliveryStatus,
    r.stream || 'Not set',
    r.stream,
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
  const workbook = XLSX.utils.book_new();
  // "Student Status" MUST be the first sheet — the existing
  // /admin/students/pathway-stream-upload page only ever reads
  // workbook.SheetNames[0] as the data sheet, so an Instructions sheet placed
  // before it would break re-upload entirely.
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Student Status');
  XLSX.utils.book_append_sheet(workbook, buildInstructionsSheet(), 'Instructions');

  const validationSheetData = [['Delivery Status', 'Stream']];
  const maxRows = Math.max(DELIVERY_STATUS_TEMPLATE_OPTIONS.length, STREAM_TEMPLATE_OPTIONS.length);
  for (let i = 0; i < maxRows; i += 1) {
    validationSheetData.push([DELIVERY_STATUS_TEMPLATE_OPTIONS[i] || '', STREAM_TEMPLATE_OPTIONS[i] || '']);
  }
  const validationSheet = XLSX.utils.aoa_to_sheet(validationSheetData);
  XLSX.utils.book_append_sheet(workbook, validationSheet, 'ValidationLists');

  workbook.Workbook = workbook.Workbook || {};
  workbook.Workbook.Sheets = workbook.Workbook.Sheets || [];
  while (workbook.Workbook.Sheets.length < workbook.SheetNames.length) {
    workbook.Workbook.Sheets.push({});
  }
  const validationIndex = workbook.SheetNames.indexOf('ValidationLists');
  if (validationIndex >= 0) {
    workbook.Workbook.Sheets[validationIndex] = { Hidden: 1 };
  }

  const totalRows = dataRows.length + 1;
  const deliveryRangeEnd = DELIVERY_STATUS_TEMPLATE_OPTIONS.length + 1;
  const streamRangeEnd = STREAM_TEMPLATE_OPTIONS.length + 1;
  // Editable columns are G (Delivery Status) and I (Stream) — the "Current …"
  // columns (F, H) are reference-only and deliberately left without dropdowns.
  worksheet['!dataValidation'] = [
    { sqref: `G2:G${totalRows}`, type: 'list', allowBlank: true, formulas: [`=ValidationLists!$A$2:$A$${deliveryRangeEnd}`] },
    { sqref: `I2:I${totalRows}`, type: 'list', allowBlank: true, formulas: [`=ValidationLists!$B$2:$B$${streamRangeEnd}`] },
  ];

  const timestamp = new Date().toISOString().split('T')[0];
  const safeSuffix = String(filenameSuffix || 'Export').replace(/[^a-z0-9]+/gi, '_');
  XLSX.writeFile(workbook, `Student_Status_${safeSuffix}_${timestamp}.xlsx`);
  return true;
}
